/* global SideDrawer, debounce, initLazyImages */

if (!customElements.get('facet-filters')) {
  class FacetFilters extends SideDrawer {
    connectedCallback() {
      this.init();
    }

    disconnectedCallback() {
      window.removeEventListener('popstate', this.historyChangeHandler);

      if (this.breakpointChangeHandler) {
        window.removeEventListener('on:breakpoint-change', this.breakpointChangeHandler);
      }
    }

    init() {
      this.filteringEnabled = this.dataset.filtering === 'true';
      this.sortingEnabled = this.dataset.sorting === 'true';
      this.form = document.getElementById('facets');
      this.results = document.getElementById('filter-results');
      this.expanded = [];

      this.handleBreakpointChange();
      this.addElements();
      this.addListeners();

      this.historyChangeHandler = this.historyChangeHandler || this.handleHistoryChange.bind(this);
      window.addEventListener('popstate', this.historyChangeHandler);
    }

    addElements() {
      if (this.filteringEnabled) {
        this.filters = this.querySelector('.facets__filters');
        this.activeFilters = this.querySelector('.facets__active-filters');
        this.activeFiltersList = this.querySelector('.active-filters');
        this.activeFiltersHeader = this.querySelector('.active-filters-header');
        this.footer = this.querySelector('.facets__footer');
      }

      if (this.sortingEnabled) {
        this.mobileSortByOptions = this.querySelectorAll('.js-drawer-sort-by');
        this.desktopSortBy = document.querySelector('.products-toolbar__sort');
      }
    }

    addListeners() {
      if (this.filteringEnabled) {
        this.breakpointChangeHandler = this.breakpointChangeHandler
          || this.handleBreakpointChange.bind(this);
        this.filters.addEventListener('click', this.handleFiltersClick.bind(this));
        this.filters.addEventListener('input', debounce(this.handleFilterChange.bind(this), 500));
        this.activeFilters.addEventListener('click', this.handleActiveFiltersClick.bind(this));
        window.addEventListener('on:breakpoint-change', this.breakpointChangeHandler);
      }

      if (this.sortingEnabled) {
        this.desktopSortBy.addEventListener('change', this.handleFilterChange.bind(this));
      }
    }

    /**
     * Handles viewport breakpoint changes.
     */
    handleBreakpointChange() {
      if (theme.mediaMatches.lg) {
        this.setAttribute('open', '');
        this.setAttribute('aria-hidden', 'false');
        this.removeAttribute('aria-modal');
        this.removeAttribute('role');
      } else {
        this.close();
        this.setAttribute('role', 'dialog');
        this.setAttribute('aria-modal', 'true');
        this.setAttribute('aria-hidden', 'true');
        this.hidden = false;
      }
    }

    /**
     * Handles 'input' events on the filters and 'change' events on the sort by dropdown.
     * @param {object} evt - Event object.
     */
    handleFilterChange(evt) {
      const formData = new FormData(this.form);
      const searchParams = new URLSearchParams(formData);
      const emptyParams = [];

      if (this.sortingEnabled) {
        let currentSortBy = searchParams.get('sort_by');

        // Keep the mobile facets form sync'd with the desktop sort by dropdown
        if (evt.target.tagName === 'CUSTOM-SELECT') {
          this.mobileSortByOptions.forEach((option) => {
            option.checked = option.value === evt.detail.selectedValue;
            currentSortBy = evt.detail.selectedValue;
          });
        }

        // Set the 'sort_by' parameter.
        searchParams.set('sort_by', currentSortBy);
      }

      // Get empty parameters.
      searchParams.forEach((value, key) => {
        if (!value) emptyParams.push(key);
      });

      // Remove empty parameters.
      emptyParams.forEach((key) => {
        searchParams.delete(key);
      });

      this.applyFilters(searchParams.toString(), evt);
    }

    /**
     * Handles 'click' events on the filters.
     * @param {object} evt - Event object.
     */
    handleFiltersClick(evt) {
      const { target } = evt;

      // Filter 'clear' button clicked.
      if (target.matches('.js-clear-filter')) {
        evt.preventDefault();
        this.applyFilters(new URL(evt.target.href).searchParams.toString(), evt);
      }

      // Filter 'show more' button clicked.
      if (target.matches('.js-show-more')) {
        const filter = target.closest('.filter');
        target.remove();

        filter.querySelectorAll('li').forEach((el) => {
          el.classList.remove('js-hidden');
        });

        if (!this.expanded.includes(filter.id)) {
          this.expanded.push(filter.id);
        }
      }
    }

    /**
     * Handles 'click' events on the active filters.
     * @param {object} evt - Event object.
     */
    handleActiveFiltersClick(evt) {
      if (evt.target.tagName !== 'A') return;
      evt.preventDefault();
      this.applyFilters(new URL(evt.target.href).searchParams.toString(), evt);
    }

    /**
     * Handles history changes (e.g. back button clicked).
     * @param {object} evt - Event object.
     */
    handleHistoryChange(evt) {
      if (evt.state !== null) {
        let searchParams = '';

        if (evt.state && evt.state.searchParams) {
          ({ searchParams } = evt.state);
        }

        this.applyFilters(searchParams, null, false);
      }
    }

    /**
     * Fetches the filtered/sorted page data and updates the current page.
     * @param {string} searchParams - Filter/sort search parameters.
     * @param {object} evt - Event object.
     * @param {boolean} [updateUrl=true] - Update url with the selected options.
     */
    async applyFilters(searchParams, evt, updateUrl = true) {
      try {
        // Disable infinite scrolling.
        const customPagination = document.querySelector('custom-pagination');
        if (customPagination) customPagination.dataset.pauseInfiniteScroll = 'true';

        // Set loading state.
        this.results.classList.add('is-loading');

        // Disable "Show X results" button until submission is complete.
        const closeBtn = this.querySelector('.js-close-drawer-mob');
        closeBtn.ariaDisabled = 'true';
        closeBtn.classList.add('is-loading');

        // Use Section Rendering API for the request, if possible.
        let fetchUrl = `${window.location.pathname}?${searchParams}`;
        if (this.form.dataset.filterSectionId) {
          fetchUrl += `&section_id=${this.form.dataset.filterSectionId}`;
        }

        // Cancel current fetch request. (Raises an exception)
        if (this.applyFiltersFetchAbortController) {
          this.applyFiltersFetchAbortController.abort('Request changed');
        }
        this.applyFiltersFetchAbortController = new AbortController();

        // Fetch filtered products markup.
        const response = await fetch(fetchUrl, {
          method: 'GET',
          signal: this.applyFiltersFetchAbortController.signal
        });

        if (response.ok) {

          const tmpl = document.createElement('template');
          tmpl.innerHTML = await response.text();



          // Restore UI state.
          this.form.querySelectorAll('details-disclosure > details').forEach((existingFilter) => {
            const target = tmpl.content.getElementById(existingFilter.id);
            if (target) {
              target.open = existingFilter.open;
            }
          });
          tmpl.content.querySelectorAll('#facets details-disclosure > details').forEach((newFilter) => {
            if (this.expanded.includes(newFilter.id)) {
              const hiddenElements = newFilter.querySelectorAll('.js-hidden');
              hiddenElements.forEach((listItem) => {
                listItem.classList.remove('js-hidden');
              });
              newFilter.querySelector('.filter__more')?.remove();
            }
          });

          // Update the filters.
          this.form.innerHTML = tmpl.content.getElementById('facets').innerHTML;

          // Update the label of the mobile filter button
          closeBtn.innerText = tmpl.content.querySelector('.js-close-drawer-mob').innerText;

          // Preserve the CSS class of the results
          const currentResultsUl = this.results.querySelector('ul');
          this.currentResultsClass = currentResultsUl ? this.results.querySelector('ul').getAttribute('class') : this.currentResultsClass;

          // Update the results.
          this.results.innerHTML = tmpl.content.getElementById('filter-results').innerHTML;

          // Set the CSS class of the results to what it was
          const newResultsUl = this.results.querySelector('ul');
          if (newResultsUl && this.currentResultsClass) newResultsUl.setAttribute('class', this.currentResultsClass);

          // Reinitialize re-rendered components.
          this.addElements();
          this.addListeners();

          // Reinitialize lazyload images after filters applied.
          if (typeof initLazyImages === 'function') initLazyImages();

          // Reinitialize any custom pagination
          if (customPagination && customPagination.reload) customPagination.reload();

          // Update the URL.
          if (updateUrl) FacetFilters.updateURL(searchParams);

          // Scroll to the top of the results if needed
          if (this.results.getBoundingClientRect().top < 0) {
            // If the header is sticky, compensate for it when scrolling to elements
            let headerHeight = 0;
            if (document.querySelector('store-header[data-is-sticky="true"]')) {
              headerHeight = Number.parseInt(
                getComputedStyle(this.parentElement)
                  .getPropertyValue('--header-height')
                  .replace('px', ''),
                10
              ) || 0;
            }
            window.scrollTo({
              top: this.results.getBoundingClientRect().top + window.scrollY - headerHeight - 45,
              behavior: 'smooth'
            });
          }

          // Enable the "Show X results" button
          closeBtn.classList.remove('is-loading');
          closeBtn.removeAttribute('aria-disabled');

          // Renable infinite scroll
          if (customPagination) customPagination.dataset.pauseInfiniteScroll = 'false';

          // Broadcast the update for anything else to hook into
          document.dispatchEvent(new CustomEvent('on:facet-filters:updated'), { bubbles: true });

          let html = `<div class="product__modal-overlay" style="display: none;">
  <div class="modal-container">
    <span class="close-btn">
      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none">
        <path d="M2 2L22 22L2 2Z" fill="#fff"/>
        <path d="M2 2L22 22M2 22L22 2" stroke="#fff" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    </span>
    <!-- 弹窗内容可以放在这里 -->
    <div class="modal-overlay__content">
      <div class="overlay__header">
        <h2>Select Your Vehicle</h2>
        <p>Provide vehicle details to confirm fitment</p>
      </div>
      <div class="vehicle-selector">
        <form id="vehicle-form">
          <div class="select-box__container">
            <label for="year-select" class="selct-box__label">1</label>
            <select id="year-select" class="select-box">
              <option value="">YEAR</option>
            </select>
          </div>
          <div class="select-box__container">
            <label for="year-select" class="selct-box__label">2</label>
            <select id="make-select" class="select-box" disabled>
              <option value="">MAKE</option>
            </select>
          </div>
          <div class="select-box__container">
            <label for="year-select" class="selct-box__label">3 </label>
            <select id="model-select" class="select-box" disabled>
              <option value="">MODEL</option>
            </select>
          </div>
          <div class="select-box__container select-box__submodels flex"></div>
          <div class="select-box__container">
            <a href="javascript:void(0)" class="cannot-find-my-vehicle">Cannot Find My Vehicle</a>
            <button type="submit" id="submit-btn" disabled>Confirm</button>
          </div>
        </form>
      </div>
      <div class="ymm-loader__container"><div class="ymm-loader"></div></div>
    </div>
  </div>
                 </div>`;
          //先移除html，再添加新的html
          document.querySelectorAll('.product__modal-overlay').forEach(element => {
            element.remove();
          });

          Array.from($('.card__fit-info')).forEach((element, index) => {
            const $element = $(element);
            let pid = $element.attr('class').substring(20)
            getProductDetail(deviceId, pid).then((data) => {
              if (data.code === 200) {
                renderYmmData(data.data, pid);
              }
            })
          })

          function renderYmmData(data, pid) {
            const tag = Number(data.tag);
            const ymm = data.ymm
            const svgIcons = {
              error: `<svg t="1727255248897" class="icon" viewBox="0 0 1024 1024" version="1.1" xmlns="http://www.w3.org/2000/svg" p-id="4311" width="16" height="16"><path d="M512 32C251.4285715625 32 32 251.4285715625 32 512s219.4285715625 480 480 480 480-219.4285715625 480-480-219.4285715625-480-480-480z m205.7142853125 617.142856875c20.5714284375 20.5714284375 20.5714284375 48 0 61.714286249999994-20.5714284375 20.5714284375-48 20.5714284375-61.714285312499996 0l-137.142856875-137.1428578125L374.857143125 717.7142853125c-20.5714284375 20.5714284375-48 20.5714284375-68.5714284375 0s-20.5714284375-54.857143125 0-68.5714284375l144-144-137.1428578125-137.142856875c-20.5714284375-13.714285312500001-20.5714284375-41.142856875 0-61.714285312499996 20.5714284375-20.5714284375 48-20.5714284375 61.714286249999994 0l137.142856875 137.142856875 144-144c20.5714284375-20.5714284375 48-20.5714284375 68.5714284375 0 20.5714284375 20.5714284375 20.5714284375 48 0 68.5714284375L580.5714284375 512l137.142856875 137.142856875z" fill="#d20000" p-id="4312"></path></svg>`,
              info: `<svg t="1726134540749" class="icon" viewBox="0 0 1024 1024" version="1.1" xmlns="http://www.w3.org/2000/svg" p-id="4560" width="14" height="14"><path d="M512 0a512 512 0 1 0 512 512A512 512 0 0 0 512 0z m0 897.365333a102.4 102.4 0 1 1 102.4-102.4 102.4 102.4 0 0 1-102.4 102.4z m0-260.949333c-68.266667 0-123.050667-318.976-123.050667-386.901333a123.050667 123.050667 0 0 1 246.101334 0c0 67.925333-54.784 386.901333-123.050667 386.901333z" fill="#4d4d4d" p-id="4561"></path></svg>`,
              check: `<svg t="1731898405348" class="icon" viewBox="0 0 1025 1024" version="1.1" xmlns="http://www.w3.org/2000/svg" p-id="4265" width="16" height="16"><path d="M511.974401 0c-282.75527 0-511.974401 229.219131-511.974401 511.974401 0 282.757318 229.219131 511.974401 511.974401 511.974401 282.757318 0 511.974401-229.217083 511.974401-511.974401C1023.948803 229.219131 794.729672 0 511.974401 0zM805.63063 379.174385 474.510162 710.296901c0 0-0.004096 0.004096-0.010239 0.010239-15.265029 15.269125-38.541433 17.652877-56.31104 7.157402-3.290971-1.945503-6.393536-4.333351-9.219635-7.157402-0.002048-0.004096-0.006144-0.006144-0.006144-0.006144l-190.642884-190.642884c-18.095223-18.095223-18.095223-47.4375 0-65.536819 18.095223-18.095223 47.4375-18.095223 65.532723 0l157.884714 157.884714 298.362298-298.362298c18.097271-18.095223 47.439548-18.095223 65.534771 0C823.725854 331.738933 823.725854 361.079162 805.63063 379.174385z" fill="#357C41" p-id="4266"></path></svg>`,
            };
            let subModels = '';
            if (data.selectedSubModels) {
              for (const key in data.selectedSubModels) {
                if (data.selectedSubModels.hasOwnProperty(key)) {
                  subModels += `${data.selectedSubModels[key]} & `;
                }
              }
            }
            const getFitInfo = () => {
              const year = data.ymm?.year || '';
              const make = data.ymm?.make || '';
              const model = data.ymm?.model || '';
              let fitInfoText = '';
              let svgIcon = '';

              switch (tag) {
                case 5:
                  svgIcon = svgIcons.error;
                  fitInfoText = `Do Not Fit Your ${year} ${make} ${model}`;
                  //获取'.card_{{ product.id }}'的父元素
                  const parent = $('.card_' + pid).parent();
                  // parent.remove();
                  break;
                case 4:
                  svgIcon = svgIcons.check;
                  fitInfoText = `Fits your ${year} ${make} ${model} ${subModels.slice(0, -2)}`;
                  break;
                case 3:
                  svgIcon = svgIcons.info;
                  fitInfoText = `Select Your Submodel`;
                  break;
                case 2:
                  svgIcon = svgIcons.check;
                  fitInfoText = `Fits your ${year} ${make} ${model}`;
                  break;
                case 1:
                  svgIcon = svgIcons.info;
                  fitInfoText = `Select Your Vehicle`;
                  break;
                case 0:
                  svgIcon = svgIcons.check;
                  fitInfoText = `Universal Fit`;
                  break;
                default:
                  fitInfoText = '';
                  svgIcon = '';
                  break;
              }

              return `
                <div class="ymm-data__fit-info" tag="${tag}">
                  ${svgIcon}
                  <span class="ymm-data__fit-info-text">${fitInfoText}</span>
                </div>
              `;
            };
            const getLocationsHtml = () => {
              if (data.locations && data.locations.length > 0) {
                const locationsList = data.locations
                  .map(
                    (location) =>
                      `<div class="ymm-data__locations-item"><span>&#8226; ${location}</span></div>`
                  )
                  .join('');
                return `
                  <div class="ymm-data__locations">
                    <p>Locations:</p>
                    <div class="ymm-data__locations-list">${locationsList}</div>
                  </div>
                `;
              }
              return '';
            };

            let htmlContent = `
              <div class="ymm-data__container" tag="${tag}" product-id="${pid}" ymm-data= ${encodeData(ymm)}>
                ${getFitInfo()}
              </div>
              <div class="product-card-notice-submodel">
              </div>
                ${(tag === 4 || tag === 2 || tag === 0) ? getLocationsHtml() : ''}
            `;
            const htmlScrollWidth = document.documentElement.scrollWidth;
            const scrollWidth = window.innerWidth - htmlScrollWidth;
            $('.card__fit-info.info_' + pid).html(htmlContent);
            if (tag !== 0) {
              $(`.card__fit-info.info_${pid} .ymm-data__fit-info`).on('click', function () {
                document.body.insertAdjacentHTML('beforeend', html);
                $('body').css('overflow', 'hidden')
                $('body').css('padding-right', scrollWidth + 'px')
                $('.product__modal-overlay').fadeIn();
                getProductModalYmmTree(pid).then((data) => {
                  if (data.code === 200) {
                    if (tag === 5 || tag === 1) {
                      renderModalYmmTree(data.data);
                    } else if (tag === 4 || tag === 3 || tag === 2) {
                      renderModalYmmTree(data.data, ymm);
                    }
                    $('.product__modal-overlay .ymm-loader__container').hide();
                  }
                })
              });
            }
          }
          closeModal('.product__modal-overlay');
          function closeModal(modalClass) {
            $(document).on('click', modalClass + ', .close-btn', function (e) {
              // 如果点击的是容器内部，不关闭弹窗
              if ($(e.target).closest('.modal-container').length === 0 || $(e.target).closest('.close-btn').length > 0) {
                $(modalClass).fadeOut();
                setTimeout(() => {
                  $('body').css('overflow', 'auto')
                  $('body').css('padding-right', 0)
                  $('.product__modal-overlay .ymm-loader__container').show();
                }, 400)
              }
              if ($(e.target).closest('.cannot-find-my-vehicle').length > 0) {
                $(modalClass).fadeOut();
                setTimeout(() => {
                  $('.product__modal-overlay .ymm-loader__container').show();
                }, 400)
              }
            });
          }
          const submitBtn = $('.product__modal-overlay #submit-btn');
          const yearSelect = $('.product__modal-overlay #year-select');
          const makeSelect = $('.product__modal-overlay #make-select');
          const modelSelect = $('.product__modal-overlay #model-select');

          function renderModalYmmTree(data, ymm = {}) {
            let vehicleData = data
            const sortedYears = Object.keys(vehicleData).filter(year => !isNaN(year)).sort((a, b) => b - a);
            // 填充年份选择框
            sortedYears.forEach(year => {
              yearSelect.append(new Option(year, year));
            });
            // 检查 YMM 数据并进行默认选择
            if (Object.keys(ymm).length > 0) {
              const { year, make, model } = ymm;
              // 如果 YMM 中有年份，品牌，型号则默认选中
              if (year && make && model) {
                yearSelect.val(year).trigger('change'); // 设置默认年份并触发改变事件
                makeSelect.prop('disabled', false); // 启用品牌选择框
                const makes = Object.keys(vehicleData[year]).sort();
                makes.forEach(makeOption => {
                  makeSelect.append(new Option(makeOption, makeOption));
                });
                makeSelect.val(make).trigger('change'); // 设置默认品牌并触发改变事件
                modelSelect.prop('disabled', false); // 启用型号选择框

                const models = vehicleData[year][make].sort();
                models.forEach(modelOption => {
                  modelSelect.append(new Option(modelOption, modelOption));
                });
                modelSelect.val(model).trigger('change'); // 设置默认型号并触发改变事件
                // 调用 checkFormCompletion 来检查表单状态
                checkFormCompletion();
              }
            }
            //当年份选择框改变时
            yearSelect.change(function () {
              const selectedYear = $(this).val();
              // 重置并禁用后续选择框
              makeSelect.empty().append(new Option('MAKE', '')).prop('disabled', true);
              modelSelect.empty().append(new Option('MODEL', '')).prop('disabled', true);
              if (selectedYear && !isNaN(selectedYear)) {
                // 启用并填充品牌选择框
                makeSelect.prop('disabled', false);
                const makes = Object.keys(vehicleData[selectedYear]).sort();
                makes.forEach(make => {
                  makeSelect.append(new Option(make, make));
                });
              }
              checkFormCompletion();
            });
            // 当品牌选择框改变时
            makeSelect.change(function () {
              const selectedYear = yearSelect.val();
              const selectedMake = $(this).val();
              // 重置并禁用型号选择框
              modelSelect.empty().append(new Option('MODEL', '')).prop('disabled', true);
              if (selectedMake) {
                // 启用并填充型号选择框
                modelSelect.prop('disabled', false);
                const models = vehicleData[selectedYear][selectedMake].sort();
                models.forEach(model => {
                  modelSelect.append(new Option(model, model));
                });
              }
              checkFormCompletion()
            });
            // 当型号选择框改变时
            modelSelect.change(function () {
              checkFormCompletion(); // 检查表单状态
            });
            // 检查表单是否完成
            function checkFormCompletion() {
              // 检查所有选择框是否有值
              const isFormComplete = yearSelect.val() && makeSelect.val() && modelSelect.val();
              if (isFormComplete) {
                getProductModalInfo(deviceId, yearSelect.val(), makeSelect.val(), modelSelect.val(), pid).then((data) => {
                  if (data.code === 200) {
                    if (Object.keys(data.data).length > 0) {
                      renderModalSubmodels(data.data)
                      if (!data.data.unselectedSubModels) {
                        submitBtn.prop('disabled', false);
                      }
                      if (!data.data.selectedSubModels) {
                        submitBtn.prop('disabled', true);
                      }
                    } else {
                      //给submiut按钮启用
                      submitBtn.prop('disabled', false);
                    }
                  }
                })
              } else {
                //清空submodels
                $('.product__modal-overlay .select-box__submodels').html('')
              }
            }
            submitBtn.off('click').click(function (e) {
              if (!submitBtn.prop('disabled')) {
                //阻止跳转链接
                e.preventDefault();
                const selectedYear = yearSelect.val();
                const selectedMake = makeSelect.val();
                const selectedModel = modelSelect.val();
                //获取所有select-box__submodels下data-selected="true"的input的value
                let subModels = []
                $('.product__modal-overlay .select-box__submodels .collection-detail__submodel-item[data-selected="true"] input').each(function () {
                  subModels.push($(this).val())
                })
                addProductModalMemory(customerEmail, deviceId, selectedYear, selectedMake, selectedModel, subModels).then((data) => {
                  let newYmm = {
                    year: selectedYear,
                    make: selectedMake,
                    model: selectedModel
                  }
                  if (data.code === 200) {
                    //判断ymm与ymmData是否一致
                    if (encodeData(newYmm) === encodeData(ymm)) {
                      //刷新页面
                      location.reload()
                    } else {
                      //将newYmm存入Cookies
                      Cookies.set('ymmData', encodeData(newYmm))
                      if (Object.keys(decodeData(getQueryParamNames()[0]) || {}).length > 0) {//说明是通过ymm进入的
                        window.location.href = `${window.location.origin}/products/{{ product.handle }}/${newYmm.year.toLowerCase().replace(/ /g, '-')}-${newYmm.make.toLowerCase().replace(/ /g, '-')}-${newYmm.model.toLowerCase().replace(/ /g, '-')}?${encodeData(newYmm)}`
                      } else {//说明是通过普通集合页进入的
                        location.reload()
                      }
                    }
                  } else if (data.code === 218) {
                    alert(data.msg)
                  }
                })
              }
            })
          }
          function renderModalSubmodels(data) {
            let num = 0;
            const container = document.querySelector('.product__modal-overlay .select-box__submodels');
            container.innerHTML = ''
            if (data.selectedSubModels) {
              //渲染选中的子模型
              data.selectedSubModels.forEach((subModel) => {
                num++;
                //生成一个div元素，存放subModelElement
                const div = document.createElement('div');
                const span = document.createElement('span');
                const spanContainer = document.createElement('div');
                spanContainer.classList.add('select-box__submodels-title');
                span.textContent = `Submodel ${num}`
                div.classList.add(`select-box__submodels-container`);
                div.classList.add('flex')
                spanContainer.appendChild(span);
                div.appendChild(spanContainer)
                div.setAttribute(`subModel${num}`, '')
                let subModelKey = subModel[`selectedSubModel${num}`]
                subModel[`subModel${num}`].forEach((sub) => {
                  Object.entries(sub).forEach(([key, imageUrl]) => {
                    let flag = false
                    if (key === subModelKey) {
                      flag = true
                    }
                    const subModelElement = createSubModelElement(key, imageUrl, flag);
                    if (imageUrl + '' == 'null') {
                      spanContainer.style.height = 'auto'
                    }
                    div.appendChild(subModelElement);
                    container.appendChild(div);
                  });
                })
                addDragScroll(div);
              })
            }
            if (data.unselectedSubModels) {
              num += 1
              //生成一个div元素，存放subModelElement
              const div = document.createElement('div');
              const span = document.createElement('span');
              const spanContainer = document.createElement('div');
              spanContainer.classList.add('select-box__submodels-title');
              span.textContent = `Submodel ${num}`
              div.classList.add(`select-box__submodels-container`);
              div.classList.add('flex')
              spanContainer.appendChild(span);
              div.appendChild(spanContainer)
              div.setAttribute(`subModel${num}`, '')
              //渲染未选中的子模型
              data.unselectedSubModels.forEach((subModel) => {
                Object.entries(subModel).forEach(([key, imageUrl]) => {
                  const subModelElement = createSubModelElement(key, imageUrl, false);
                  if (imageUrl + '' == 'null') {
                    spanContainer.style.height = 'auto'
                  }
                  div.appendChild(subModelElement);
                  container.appendChild(div);
                });
              })
              addDragScroll(div);
            }
          }
          function createSubModelElement(key, imageUrl, isSelected) {
            // 创建一个标签用于包含单选框和图片
            const wrapper = document.createElement('label');
            wrapper.classList.add('collection-detail__submodel-item');
            wrapper.classList.add('flex');
            if (isSelected) {
              wrapper.setAttribute('data-selected', true);
            } else {
              wrapper.setAttribute('data-selected', false);
            }
            // 创建单选按钮
            const radio = document.createElement('input');
            radio.type = 'radio';
            radio.name = 'subModel';
            radio.value = key;
            radio.checked = isSelected;
            radio.style.display = 'none';

            // 绑定 change 事件监听器
            radio.addEventListener('click', function () {
              // 获取当前元素的 data-selected 属性
              let status = wrapper.getAttribute('data-selected');
              // 如果已经是选中的状态，直接返回，不执行后续逻辑
              if (status === 'true') {
                return; // 阻止函数继续执行
              }
              // 给当前元素的父元素select-box__submodels-container内所有的collection-detail__submodel-item的状态变成false
              $(this).closest('.select-box__submodels-container').find('.collection-detail__submodel-item').each(function () {
                $(this).attr('data-selected', 'false');
              });
              // 将当前元素设置为选中状态
              wrapper.setAttribute('data-selected', 'true');
              // 获取所有 data-selected="true" 的子元素下的 input 的值
              let subModels = [];
              $(this).closest('.select-box__submodels-container').prevAll('.select-box__submodels-container').addBack().find('.collection-detail__submodel-item[data-selected="true"] input').each(function () {
                subModels.push($(this).val());
              });
              // 调用 getProductModalInfo 并传递选中的 subModels
              getProductModalInfo(deviceId, yearSelect.val(), makeSelect.val(), modelSelect.val(), pid, subModels).then(data => {
                if (data.code === 200) {
                  if (Object.keys(data.data).length > 0) {
                    renderModalSubmodels(data.data);

                    // 启用或禁用提交按钮
                    if (!data.data.unselectedSubModels) {
                      submitBtn.prop('disabled', false);
                    }
                    if (!data.data.selectedSubModels) {
                      submitBtn.prop('disabled', true);
                    }
                  } else {
                    // 如果没有数据，启用提交按钮
                    submitBtn.prop('disabled', false);
                  }
                }
              });
            });


            // 创建图像元素
            const img = document.createElement('img');
            img.src = imageUrl;
            img.alt = key;
            // 创建显示模型名称的元素
            const label = document.createElement('span');
            label.classList.add('collection-detail__submodel-name');
            label.textContent = key;
            // 将图像、单选按钮和标签添加到包装器中
            if (imageUrl + '' !== 'null') {
              wrapper.appendChild(img);
            }
            wrapper.appendChild(radio);
            wrapper.appendChild(label);
            const span = document.createElement('span');
            //设置span元素的文字内容
            span.classList.add('collection-detail__submodel-selected');
            span.innerHTML =
              '<svg t="1725947716636" class="icon" viewBox="0 0 1024 1024" version="1.1" xmlns="http://www.w3.org/2000/svg" p-id="5468" width="50" height="60"><path d="M997.888 70.144C686.592 261.12 460.8 502.272 358.912 623.104l-248.832-195.072-110.08 88.576 429.568 437.248c73.728-189.44 308.224-559.616 594.432-822.784l-26.112-60.928m0 0z" p-id="5469" fill="#01c221"></path></svg>';
            wrapper.appendChild(span);
            return wrapper;
          }
        }
      } catch (error) {
        console.warn(error);
      } finally {
        this.results.classList.remove('is-loading');
      }
    }

    /**
     * Updates the url with the current filter/sort parameters.
     * @param {string} searchParams - Filter/sort parameters.
     */
    static updateURL(searchParams) {
      window.history.pushState(
        { searchParams },
        '',
        `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`
      );
    }
  }

  customElements.define('facet-filters', FacetFilters);
}
